import React, { useEffect, useState } from 'react'
import { Modal, Row, Col, Form } from 'react-bootstrap'
import { Button, InputField } from '@components/common'
import { Claim } from '@dataTypes/claimFeature.types'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import {
  getClaimStatusExternal,
  updateClaimStatusById,
} from '@services/api/claims.api'
import { useSelector } from 'react-redux'
import { RootState } from '@store/index'
import { useAppSelector } from '@store/hooks'
import {
  checkTransactionIfExists,
  testWebhook,
} from '@services/api/tracking.api'
import { getTrackingCheckStatus } from '@utils/tracking.util'
import { logger } from '@utils/safeLogger'

type ClaimsSimulationEditorProps = {
  show: boolean
  onHide: () => void
  data: any
  onUpdate: () => void
}

const claimStatusOptions = [
  {
    label: 'Under Review',
    value: 'Under Review',
    code: 'A0',
    statusCode: '16',
    codeDescription:
      'Acknowledgement/Forwarded-The claim/encounter has been forwarded to another entity.',
    statusCodeDescription: 'Claim/encounter has been forwarded to entity.',
  },
  {
    label: 'Approved',
    value: 'Approved',
    code: 'F1',
    statusCode: '0',
    codeDescription: 'Finalized/Payment-The claim/line has been paid.',
    statusCodeDescription: 'Cannot provide further status electronically.',
  },
  {
    label: 'Partially Approved',
    value: 'Partially Approved',
    code: 'A2',
    statusCode: '0',
    codeDescription:
      'Acknowledgement/Acceptance into adjudication system-The claim/encounter has been accepted into the adjudication system.',
    statusCodeDescription: 'Cannot provide further status electronically.',
  },
  {
    label: 'Rejected',
    value: 'Rejected',
    code: 'A3',
    statusCode: '0',
    codeDescription:
      'Acknowledgement/Returned as unprocessable claim-The claim/encounter has been rejected and has not been entered into the adjudication system.',
    statusCodeDescription: 'Cannot provide further status electronically.',
  },
]

const ClaimsSimulationEditor: React.FC<ClaimsSimulationEditorProps> = ({
  show,
  onHide,
  data,
  onUpdate,
}) => {
  const { data: claims } = useSelector((state: RootState) => state.claims)
  const [filteredClaims, setFilteredClaims] = useState<any[]>([])
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null)
  const [selectedClaimStatus, setSelectedClaimStatus] = useState<any>(null)
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState<any>({
    modeOfPayment: '',
    checkEFTNumber: '',
    checkStatus: '',
  })
  const [paymentIsOnProcess, setPaymentIsOnProcess] = useState<boolean>(false)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()

  useEffect(() => {
    if (data) {
      const availableClaims: any[] = []
      ;(claims as any).claims.forEach((claim: any) => {
        const foundClaim = data.find((x: any) => x.id === claim.claim_id)
        let foundTracking = {} as any
        let processingTransaction = false
        if (foundClaim) {
          const claimStatus = (claim.claimstatus as any[]).find(
            (x) => x.claim_id === claim.claim_id,
          )
          if (claimStatus) {
            const match = claimStatus.xml.match(
              /<ChequeNum[^>]*>([\w\-_]+)<\/ChequeNum>/,
            )
            const reference_number = match ? match[1] : ''
            const tracking = (claims as any).tracking.find(
              (x: any) => x.reference_number === reference_number,
            )
            if (tracking) {
              if (getTrackingCheckStatus(tracking) === 'Processing') {
                foundTracking = tracking
                processingTransaction = true
              }
            }
          }
        }
        const isIncluded =
          (foundClaim && foundClaim.generalStatus === 'Under Review') ||
          (foundClaim && processingTransaction)

        if (isIncluded) {
          availableClaims.push({ ...foundClaim, paymentDetails: foundTracking })
        }
      })

      setFilteredClaims(availableClaims)
    }
  }, [claims, data])

  useEffect(() => {
    if (selectedClaim) {
      if (
        (selectedClaim as any).paymentDetails &&
        (selectedClaim as any).paymentDetails?.tracking_transaction_id
      ) {
        const paymentDetails = (selectedClaim as any).paymentDetails
        setSelectedPaymentDetails({
          modeOfPayment: paymentDetails?.payment_method,
          checkEFTNumber: paymentDetails?.reference_number,
          checkStatus: paymentDetails?.check_status,
        })
        setSelectedClaimStatus(
          claimStatusOptions.find(
            (status) => status.value === selectedClaim.generalStatus,
          ),
        )
        setPaymentIsOnProcess(true)
      } else {
        setSelectedPaymentDetails({
          modeOfPayment: '',
          checkEFTNumber: '',
          checkStatus: '',
        })
        setSelectedClaimStatus('')
        setPaymentIsOnProcess(false)
      }
    }
  }, [selectedClaim])

  const handleSelectClaim = (claimId: string): void => {
    const claim = filteredClaims.find((x: Claim) => x.id === claimId)
    setSelectedClaim(claim || null)
  }

  const getPaidAmount = (
    submittedAmount: number | string,
  ): number | string | null => {
    if (submittedAmount === null || submittedAmount === undefined) {
      return null
    }

    const isNumber = typeof submittedAmount === 'number'
    const numericAmount = Number(submittedAmount)

    if (Number.isNaN(numericAmount)) {
      return null
    }

    let calculated: number | null = null

    switch (selectedClaimStatus.value) {
      case 'Approved':
        calculated = numericAmount
        break

      case 'Partially Approved':
        calculated = numericAmount * 0.6
        break

      case 'Rejected':
      case 'Under Review':
      default:
        return null
    }

    calculated = Number(calculated.toFixed(2))

    return isNumber ? calculated : calculated.toFixed(2)
  }

  const formRequestData = async (): Promise<any> => {
    const dataForMockService = {
      claimDetails: {
        claimId: selectedClaim?.providerClaimId,
        payerName: selectedClaim?.insuranceCarrier,
        payerIdCode: selectedClaim?.insuranceCarrierPayerCode,
        billingProvider: {
          lastName: selectedClinic?.clinic_name || '',
        },
        renderingProvider: {
          lastName: selectedClaim?.providerName
            .replace(/^Dr\.\s*/i, '')
            .trim()
            .split(/\s+/)
            .slice(-1)[0],
          firstName: selectedClaim?.providerName
            .replace(/^Dr\.\s*/i, '')
            .trim()
            .split(/\s+/)[0],
        },
        subscriber: {
          lastName: selectedClaim?.subscriberLastName,
          firstName: selectedClaim?.subscriberFirstName,
          dob: selectedClaim?.subscriberDateOfBirth,
          memberId: selectedClaim?.subscriberId,
        },
        patient: {
          lastName: selectedClaim?.patientLastName,
          firstName: selectedClaim?.patientFirstName,
          dob: selectedClaim?.patientDateOfBirth,
          memberId: selectedClaim?.patientId,
        },
        submittedAmount: Number(selectedClaim?.claimAmount),
        paidAmount:
          selectedClaimStatus?.value === 'Partially Approved' ||
          selectedClaimStatus?.value === 'Approved'
            ? getPaidAmount(
                Number(
                  selectedClaim?.procedures?.reduce(
                    (sum, procedure) =>
                      sum + (Number(procedure.estimated_insurance_pay) || 0),
                    0,
                  ) ?? 0,
                ),
              )
            : 0,
        statusDate: new Date(
          Date.UTC(
            new Date().getUTCFullYear(),
            new Date().getUTCMonth(),
            new Date().getUTCDate(),
          ),
        )
          .toISOString()
          .replace('Z', '-00:00'),
        categoryCodes: [
          {
            code: selectedClaimStatus?.code,
            statusCode: selectedClaimStatus?.statusCode,
            codeDescription: selectedClaimStatus?.codeDescription,
            statusCodeDescription: selectedClaimStatus?.statusCodeDescription,
          },
        ],
        hasAttachment: false,
        claimItems: selectedClaim?.procedures.map((procedure) => ({
          procedureCode: procedure.procedureCode,
          startDate: procedure.procedureDate.replace(
            /(\d{2})\/(\d{2})\/(\d{4})/,
            '$3-$1-$2T00:00:00-00:00',
          ),
          endDate: procedure.procedureDate.replace(
            /(\d{2})\/(\d{2})\/(\d{4})/,
            '$3-$1-$2T00:00:00-00:00',
          ),
          submittedAmount: procedure.amount,
          ...((selectedClaimStatus?.value === 'Partially Approved' ||
            selectedClaimStatus?.value === 'Approved') && {
            paidAmount: getPaidAmount(procedure.estimated_insurance_pay),
          }),
          statusDate: new Date(
            Date.UTC(
              new Date().getUTCFullYear(),
              new Date().getUTCMonth(),
              new Date().getUTCDate(),
            ),
          )
            .toISOString()
            .replace('Z', '-00:00'),
          categoryCodes: [
            {
              code: selectedClaimStatus?.code,
              statusCode: selectedClaimStatus?.statusCode,
              codeDescription: selectedClaimStatus?.codeDescription,
              statusCodeDescription: selectedClaimStatus?.statusCodeDescription,
            },
          ],
        })),
      },
    }
    return {
      providerClaimId: selectedClaim?.providerClaimId || '',
      ...dataForMockService,
    }
  }

  const handleUpdateClaim = async (): Promise<void> => {
    try {
      showLoading({
        container: '.claims-simulation-editor-modal',
      })
      const requestData = await formRequestData()
      if (
        selectedClaimStatus?.value === 'Approved' ||
        selectedClaimStatus?.value === 'Partially Approved'
      ) {
        requestData.payment_details = {
          claim_public_id: selectedClaim?.providerClaimId,
          reference_number: selectedPaymentDetails.checkEFTNumber,
          payment_method: selectedPaymentDetails.modeOfPayment,
          check_status: selectedPaymentDetails.checkStatus,
        }
        if (!paymentIsOnProcess) {
          const transactionExists = await checkTransactionIfExists(
            'reference_number',
            selectedPaymentDetails.checkEFTNumber,
            true,
          )
          if (transactionExists?.data?.result?.exists) {
            setTimeout(() => {
              hideLoading('.claims-simulation-editor-modal')
              showToast(
                createFailedToast(
                  'Reference number already exists',
                  'Reference number already exists. Please try again with a different reference number.',
                ),
              )
            }, 2000)
            return
          }
        }
      }
      await getClaimStatusExternal(requestData)
        .then(async (response) => {
          requestData.status = selectedClaimStatus.value
          requestData.insurancePlanId = selectedClaim?.insurancePlanId
          requestData.externalData = response.data.response
          await updateClaimStatusById(selectedClaim?.id || '', requestData)

          if (
            selectedClaimStatus?.value === 'Approved' ||
            selectedClaimStatus?.value === 'Partially Approved'
          ) {
            testWebhook({
              claim_public_id: selectedClaim?.providerClaimId,
              reference_number: selectedPaymentDetails.checkEFTNumber,
              payment_method: selectedPaymentDetails.modeOfPayment,
              check_status: selectedPaymentDetails.checkStatus,
            })
          }
        })
        .catch((error) => {
          logger.error('Error submitting claim:', error)
        })
      setTimeout(() => {
        hideLoading('.claims-simulation-editor-modal')
        onUpdate()
        showToast(
          createSuccessToast(
            'Claim updated successfully',
            'Claim has been updated successfully.',
          ),
        )
        setSelectedClaim(null)
        setSelectedClaimStatus('')
      }, 2000)
    } catch (error) {
      logger.error('Error submitting claim:', error)
      setTimeout(() => {
        hideLoading('.claims-simulation-editor-modal')
        showToast(
          createFailedToast(
            'Claim not updated',
            'Claim not updated. Please try again.',
          ),
        )
      }, 2000)
    }
  }

  const isDisabledUpdate = (): boolean => {
    let isDisabled = !selectedClaim || !selectedClaimStatus

    if (
      selectedClaimStatus?.value === 'Approved' ||
      selectedClaimStatus?.value === 'Partially Approved'
    ) {
      isDisabled =
        !selectedPaymentDetails.modeOfPayment ||
        !selectedPaymentDetails.checkEFTNumber ||
        !selectedPaymentDetails.checkStatus
    }

    return isDisabled
  }

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        centered
        className="claims-simulation-editor-modal"
      >
        <Modal.Body>
          <Row>
            <Col md={6}>
              <h5>Claims simulation editor</h5>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group controlId="formGender">
                <Form.Label>Claim ID</Form.Label>
                <Form.Select
                  value={selectedClaim?.id}
                  onChange={(e) => handleSelectClaim(e.target.value)}
                >
                  <option value="">Select</option>
                  {filteredClaims.map((claim: any) => (
                    <option key={claim.id} value={claim.id}>
                      {claim.providerClaimId}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} key={selectedClaimStatus?.value}>
              <Form.Group controlId="formGender">
                <Form.Label>Claim status</Form.Label>
                <Form.Select
                  value={selectedClaimStatus?.value}
                  onChange={(e) =>
                    setSelectedClaimStatus(
                      claimStatusOptions.find(
                        (status) => status.value === e.target.value,
                      ),
                    )
                  }
                  disabled={paymentIsOnProcess}
                >
                  <option value="">Select</option>
                  {claimStatusOptions.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          {selectedClaim &&
            (selectedClaimStatus?.value === 'Approved' ||
              selectedClaimStatus?.value === 'Partially Approved') && (
              <>
                <Row className="mt-4 mb-2">
                  <Col>
                    <Form.Group controlId="modeOfPayment">
                      <Form.Label>Mode of Payment</Form.Label>
                      <Form.Select
                        name="modeOfPayment"
                        value={selectedPaymentDetails.modeOfPayment}
                        onChange={(e) =>
                          setSelectedPaymentDetails({
                            ...selectedPaymentDetails,
                            modeOfPayment: e.target.value,
                          })
                        }
                        disabled={paymentIsOnProcess}
                      >
                        <option value="">Select Mode of Payment</option>
                        <option value="CHECK">CHECK</option>
                        <option value="ACH">ACH</option>
                        <option value="VIRTUAL_CARD">VIRTUAL_CARD</option>
                        <option value="WIRE">WIRE</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group controlId="checkEFTNumber">
                      <Form.Label>Check/EFT/Ref No.</Form.Label>
                      <InputField
                        value={selectedPaymentDetails.checkEFTNumber}
                        onChange={(e) =>
                          setSelectedPaymentDetails({
                            ...selectedPaymentDetails,
                            checkEFTNumber: e.target.value,
                          })
                        }
                        placeholder="Enter Check/EFT/Ref No."
                        disabled={paymentIsOnProcess}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Form.Group controlId="checkStatus">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        name="checkStatus"
                        value={selectedPaymentDetails.checkStatus}
                        onChange={(e) =>
                          setSelectedPaymentDetails({
                            ...selectedPaymentDetails,
                            checkStatus: e.target.value,
                          })
                        }
                      >
                        <option value="">Select Status</option>
                        {paymentIsOnProcess ? (
                          <option value="Deposited">Deposited</option>
                        ) : (
                          <>
                            <option value="Processing">Processing</option>
                            <option value="Deposited">Deposited</option>
                          </>
                        )}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
        </Modal.Body>
        <Modal.Footer>
          <Button size="md" className="button-secondary" onClick={onHide}>
            Close
          </Button>
          <Button
            size="md"
            className="button-primary"
            onClick={() => handleUpdateClaim()}
            disabled={isDisabledUpdate()}
          >
            Update
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ClaimsSimulationEditor
